/**
 * @fileoverview Query hooks for the Sales Performance screen.
 * @description One hook per read: the summary, the ranking and the pending targets, each keyed by
 * the selected period and team so a changed filter refetches it, and each keeping the previous
 * result on screen while it loads (callers read `isPlaceholderData` to show that what is on screen
 * belongs to the previous selection). The ranking and pending lists are only mounted once the
 * summary has loaded, so a page never asks for a list before it knows the scope.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQuery } from '@tanstack/react-query'

import { SALES_CRM_QUERY_KEYS } from '@/features/sales/constants/prospects-query-keys.constants'
import {
  getSalesPerformancePending,
  getSalesPerformanceRanking,
  getSalesPerformanceSummary,
} from '@/features/sales/services/sales-performance.service'

import type { SalesPerformanceQuery } from '@/features/sales/types/sales-performance.types'

/**
 * @description Fetches the KPIs, monthly series, team comparison and filter options.
 * @param {SalesPerformanceQuery} query - The selected period and team.
 * @returns {ReturnType<typeof useQuery>} The query (data is the summary).
 */
export function useSalesPerformanceSummary(query: SalesPerformanceQuery) {
  return useQuery({
    queryKey: SALES_CRM_QUERY_KEYS.performanceSummary(query),
    queryFn: () => getSalesPerformanceSummary(query),
    placeholderData: (previous) => previous,
  })
}

/**
 * @description Fetches the individual ranking.
 * @param {SalesPerformanceQuery} query - The selected period and team.
 * @returns {ReturnType<typeof useQuery>} The query (data is the ranking).
 */
export function useSalesPerformanceRanking(query: SalesPerformanceQuery) {
  return useQuery({
    queryKey: SALES_CRM_QUERY_KEYS.performanceRanking(query),
    queryFn: () => getSalesPerformanceRanking(query),
    placeholderData: (previous) => previous,
  })
}

/**
 * @description Fetches the pending targets.
 * @param {SalesPerformanceQuery} query - The selected period and team.
 * @returns {ReturnType<typeof useQuery>} The query (data is the pending list).
 */
export function useSalesPerformancePending(query: SalesPerformanceQuery) {
  return useQuery({
    queryKey: SALES_CRM_QUERY_KEYS.performancePending(query),
    queryFn: () => getSalesPerformancePending(query),
    placeholderData: (previous) => previous,
  })
}
